import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import useAdminContext from "../../store/contexts/AdminContext";
import UserList from "./UserList";
import UserDetailModal from "./UserDetailModal";
import UserBanModal from "./UserBanModal";
import { ADMIN_ROLES } from "../../constants/types";

const UserManagement = () => {
  const {
    admin,
    authState: { loading, error },
    getUsersListApiCall,
    getUserByIdApiCall,
    updateUserApiCall,
    toggleUserStatusApiCall,
    clearErrors,
    showSnackbar,
  } = useAdminContext();

  // ============= RBAC HELPER =============
  const isSuperAdmin = () => admin?.role === ADMIN_ROLES.SUPER_ADMIN;

  // State for users and pagination
  const [users, setUsers] = useState([]);
  const [userPagination, setUserPagination] = useState({
    total: 0,
    pageNo: 1,
    pageSize: 20,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Filter states
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    account_type: "all",
    is_banned: "all",
    pageNo: 1,
    pageSize: 20,
  });

  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetailModalOpen, setUserDetailModalOpen] = useState(false);
  const [userBanModalOpen, setUserBanModalOpen] = useState(false);
  const [banAction, setBanAction] = useState(null); // 'ban' or 'unban'

  // Load users on component mount and filter changes
  useEffect(() => {
    clearErrors();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.pageNo, filters.pageSize]);

  const loadUsers = async (customFilters = filters) => {
    try {
      const result = await getUsersListApiCall(customFilters);
      if (result) {
        setUsers(result.users || []);
        setUserPagination(result.pagination || {});
      }
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value,
      pageNo: filterName !== "pageNo" ? 1 : value, // Reset to page 1 when changing filters
    };
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    loadUsers(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      search: "",
      status: "all",
      account_type: "all",
      is_banned: "all",
      pageNo: 1,
      pageSize: 20,
    };
    setFilters(clearedFilters);
    loadUsers(clearedFilters);
  };

  const handleRefresh = () => {
    loadUsers();
  };

  const handlePreviousPage = () => {
    if (userPagination.hasPrev) {
      handleFilterChange("pageNo", userPagination.pageNo - 1);
    }
  };

  const handleNextPage = () => {
    if (userPagination.hasNext) {
      handleFilterChange("pageNo", userPagination.pageNo + 1);
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const user = await getUserByIdApiCall(userId);
      if (user) {
        setSelectedUser(user);
        setUserDetailModalOpen(true);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const handleEditUser = async (userId, updateData) => {
    try {
      const updatedUser = await updateUserApiCall(userId, updateData);
      if (updatedUser) {
        // Update the user in the list
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId ? { ...user, ...updatedUser } : user
          )
        );
        setUserDetailModalOpen(false);
        setSelectedUser(null);
      }
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleBanUser = (user, action) => {
    // RBAC: Only super admins can ban/unban users
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can ban or unban users", "error");
      return;
    }

    setSelectedUser(user);
    setBanAction(action);
    setUserBanModalOpen(true);
  };

  const handleConfirmBanAction = async (reason) => {
    if (!selectedUser || !banAction) return;

    // RBAC: Double-check super admin status
    if (!isSuperAdmin()) {
      showSnackbar("Only super admins can perform this action", "error");
      return;
    }

    try {
      const isBanning = banAction === "ban";
      const result = await toggleUserStatusApiCall(selectedUser.id, {
        is_banned: isBanning,
        reason: isBanning ? reason : undefined,
      });

      if (result) {
        // Update the user in the list
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === selectedUser.id ? { ...user, ...result } : user
          )
        );
        setUserBanModalOpen(false);
        setSelectedUser(null);
        setBanAction(null);
      }
    } catch (error) {
      console.error("Error toggling user ban status:", error);
    }
  };

  const handleCloseModals = () => {
    setUserDetailModalOpen(false);
    setUserBanModalOpen(false);
    setSelectedUser(null);
    setBanAction(null);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" color="text.primary" gutterBottom>
            User Management
          </Typography>
          <Typography variant="body1" color="text.primary">
            {isSuperAdmin()
              ? "Manage system users, view profiles, and moderate accounts."
              : "View user profiles and manage user information (Limited ban/unban access)."}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* RBAC Warning for Regular Admins */}
      {!isSuperAdmin() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have limited access to user management. Only super admins can ban
          or unban users. You can view user details and update basic
          information.
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by name, email, or username..."
              size="small"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="account-type-select-label">
                Account Type
              </InputLabel>
              <Select
                labelId="account-type-select-label"
                label="Account Type"
                value={filters.account_type}
                onChange={(e) =>
                  handleFilterChange("account_type", e.target.value)
                }
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
                <MenuItem value="elite">Elite</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="ban-status-select-label">Ban Status</InputLabel>
              <Select
                labelId="ban-status-select-label"
                label="Ban Status"
                value={filters.is_banned}
                onChange={(e) =>
                  handleFilterChange("is_banned", e.target.value)
                }
              >
                <MenuItem value="all">All Users</MenuItem>
                <MenuItem value="true">Banned</MenuItem>
                <MenuItem value="false">Not Banned</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<FilterListIcon />}
                onClick={handleApplyFilters}
                disabled={loading}
                fullWidth
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                fullWidth
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* User List */}
      <UserList
        users={users}
        loading={loading}
        pagination={userPagination}
        onViewUser={handleViewUser}
        onBanUser={handleBanUser}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        isSuperAdmin={isSuperAdmin()}
      />

      {/* User Detail Modal */}
      <UserDetailModal
        open={userDetailModalOpen}
        user={selectedUser}
        onClose={handleCloseModals}
        onSave={handleEditUser}
        loading={loading}
      />

      {/* User Ban Modal - Only for Super Admins */}
      {isSuperAdmin() && (
        <UserBanModal
          open={userBanModalOpen}
          user={selectedUser}
          action={banAction}
          onClose={handleCloseModals}
          onConfirm={handleConfirmBanAction}
          loading={loading}
        />
      )}
    </Box>
  );
};

export default UserManagement;
